/**
 * Tri CLIENT des cartes de dossier (grille de dossiers, Lot H).
 *
 * La liste des dossiers est PETITE (facette de 1er niveau) → on trie côté client
 * plutôt que de multiplier les endpoints. Deux critères seulement (le `SortMenu`
 * photo, avec ses critères Vues/Note/Type/Aléatoire, ne s'applique PAS aux dossiers —
 * les cartes restent triées par nom/date) :
 *  - `name` : par nom de dossier, insensible casse/accents (`localeCompare`).
 *  - `date` : par `latestTakenAt` (date de prise de vue la plus récente du dossier).
 *
 * Fonctions PURES & testées. Le tri est STABLE (départage par nom puis par `path`
 * brut → ordre déterministe même à égalité, y compris pour la racine `''`).
 *
 * GÉNÉRIQUES sur la forme minimale nécessaire (`FolderSortable`) plutôt que sur
 * `FolderFacet` : depuis XPHOTO-32 l'onglet Dossiers affiche des `FolderTreeEntry`
 * (facette SQL + index des dossiers), et les deux se trient exactement pareil. Le
 * générique PRÉSERVE le type d'entrée en sortie — un `FolderTreeEntry[]` trié reste un
 * `FolderTreeEntry[]`, ses champs `indexed`/`hasSubfolders` ne sont pas rabotés au passage.
 */

/** Tout ce dont ce module a besoin pour trier/filtrer un dossier. */
export interface FolderSortable {
  path: string;
  latestTakenAt: string | null;
}

/** Critère de tri des cartes de dossier. */
export type FolderSortKey = 'name' | 'date';

/** Sens de tri (réutilise la même sémantique que la galerie). */
export type FolderSortDir = 'asc' | 'desc';

/**
 * Comparateur de noms insensible casse/accents. `''` (racine) trie de façon stable
 * — il compare comme une chaîne vide (donc tout en tête en ASC), départagé ensuite.
 */
function compareNames(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

/**
 * Compare deux dossiers par date (`latestTakenAt`). Les dossiers SANS date (null)
 * sont toujours rejetés en FIN de liste, quel que soit le sens (comme `NULLS LAST`
 * de la galerie) → un dossier daté passe toujours devant un dossier sans date.
 * Retourne `null` quand la comparaison ne tranche pas (mêmes dates, ou deux null)
 * pour laisser le départage stable opérer.
 */
function compareDates(a: FolderSortable, b: FolderSortable, dir: FolderSortDir): number | null {
  const ta = a.latestTakenAt;
  const tb = b.latestTakenAt;
  if (ta === null && tb === null) return null;
  if (ta === null) return 1; // a sans date → après b (toujours en fin)
  if (tb === null) return -1; // b sans date → après a
  if (ta === tb) return null;
  const cmp = ta < tb ? -1 : 1;
  return dir === 'asc' ? cmp : -cmp;
}

/**
 * Renvoie une COPIE triée des facettes de dossier selon le critère + sens donnés.
 * Tri stable : départage par nom insensible casse/accents puis par `path` brut.
 */
export function sortFolders<T extends FolderSortable>(
  folders: readonly T[],
  key: FolderSortKey,
  dir: FolderSortDir,
): T[] {
  const copy = [...folders];
  copy.sort((a, b) => {
    if (key === 'date') {
      const byDate = compareDates(a, b, dir);
      if (byDate !== null) return byDate;
      // Égalité de dates : départage STABLE par nom ASC puis path (indépendant du sens).
      const byName = compareNames(a.path, b.path);
      return byName !== 0 ? byName : a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
    }
    // key === 'name'
    const byName = compareNames(a.path, b.path);
    const signed = dir === 'asc' ? byName : -byName;
    if (signed !== 0) return signed;
    // Départage stable par `path` brut (ex. accents distincts, même base).
    return a.path < b.path ? -1 : a.path > b.path ? 1 : 0;
  });
  return copy;
}

/**
 * Filtre client des cartes par nom de dossier (réutilise la boîte de recherche `q`).
 * Insensible casse/accents. Requête vide → toutes les facettes. La racine (`''`) ne
 * correspond qu'à une requête vide (pas de nom à matcher).
 */
export function filterFolders<T extends FolderSortable>(folders: readonly T[], query: string): T[] {
  const needle = normalize(query.trim());
  if (needle === '') return [...folders];
  return folders.filter((f) => normalize(f.path).includes(needle));
}

/** Minuscule sans diacritiques (comparaison tolérante). */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
